import { useRef, useState } from "react";
import { css } from "@emotion/react";
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { v4 } from "uuid";

import type { RioConfiguration } from "@thalamos/aspire-common";

import { apiHooks } from "../../api.js";
import { routeFns } from "../../routePaths.js";
import { breakpoints } from "../../styles/themeCommon.js";
import type { MenuOptionsType } from "~/components/design-system/index.js";
import {
  Banner,
  BannerList,
  Button,
  ButtonIcon,
  MenuFlyout,
} from "~/components/design-system/index.js";

function RioInstanceRow({ rioInstance }: { rioInstance: RioConfiguration }) {
  const cardRef = useRef(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();

  const options: MenuOptionsType[] = [
    {
      name: "Edit",
      onClick: () => {
        navigate(routeFns.rioInstanceCreateEdit(rioInstance.instanceId));
      },
      icon: "edit",
      disabled: false,
    },
  ];

  return (
    <TableRow>
      <TableCell>{rioInstance.displayName}</TableCell>
      <TableCell>{rioInstance.instanceId}</TableCell>
      <TableCell>
        <Box ref={cardRef} onClick={() => setIsMenuOpen(true)}>
          <MenuFlyout
            cardRef={cardRef}
            options={options}
            isOpen={isMenuOpen}
            onClose={() => setIsMenuOpen(false)}
          />
        </Box>
      </TableCell>
    </TableRow>
  );
}

export function RioInstancesManagementPage() {
  const [rioInstancesFilterQuery, setRioInstancesFilterQuery] = useState("");
  const [page, setPage] = useState(0);
  const rioInstancesPageSize = 10;
  const theme = useTheme();
  const navigate = useNavigate();
  const [{ data: rioInstances, loading: loadingRioInstances, error }] =
    apiHooks.rio.search(
      rioInstancesFilterQuery,
      rioInstancesPageSize,
      page * rioInstancesPageSize,
    );

  const numPages =
    rioInstances && rioInstances.count
      ? Math.ceil(rioInstances.count / rioInstancesPageSize)
      : 1;

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          width: "100%",
          marginTop: "1em",
          marginBottom: "1em",
        }}
      >
        <TextField
          placeholder={"Filter rio instances..."}
          sx={{ width: "100%" }}
          value={rioInstancesFilterQuery}
          onChange={(e) => {
            setRioInstancesFilterQuery(e.target.value);
            setPage(0);
          }}
        />
        <Box
          css={css`
            @media (max-width: ${breakpoints.values.sm}px) {
              margin-left: ${theme.spacing(2)};
            }

            display: flex;
            margin-bottom: ${theme.spacing(2)};
            margin-left: ${theme.spacing(2)};
          `}
        >
          <Button
            variant={"contained"}
            label={"Create"}
            endIcon={ButtonIcon.add}
            onClick={() => navigate(routeFns.rioInstanceCreateEdit(v4()))}
          />
        </Box>
      </Box>
      {!rioInstances && loadingRioInstances && (
        <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
          <CircularProgress />
        </Box>
      )}

      {!loadingRioInstances && error && (
        <Banner bannerType={BannerList.ERROR} title={error.message} />
      )}

      {!loadingRioInstances &&
        rioInstances &&
        (rioInstances.results.length > 0 ? (
          <Box>
            <Box
              sx={{ width: "100%", display: "flex", justifyContent: "center" }}
            >
              <button onClick={() => page > 0 && setPage(page - 1)}>
                &#60;
              </button>
              <Box sx={{ marginLeft: "1em", marginRight: "1em" }}>
                Page {page + 1} of {numPages}
              </Box>
              <button onClick={() => page < numPages - 1 && setPage(page + 1)}>
                &#62;
              </button>
            </Box>
            <TableContainer>
              <Table size="small" aria-label="a dense table">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Display Name</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Instance Id</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rioInstances.results?.map((rioInstance, index) => (
                    <RioInstanceRow key={index} rioInstance={rioInstance} />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ) : (
          <p>no rio instances found</p>
        ))}
    </Box>
  );
}
